///
/// EarthBrowser
///
/// Buoy Data Provider
///
/// Provides buoy data from local dataset sync database with custom rendering
///
/// Created by Matt Giger on December 21, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type { BuoyEntry } from '@shared-types/buoy'
import { formatDistanceToNow } from 'date-fns'
import { AlertTriangle } from 'lucide-react'
import type { ColumnMetadata, DataQuery, DataRow, DataStats, FilterGroup, IDataProvider, ProviderCapabilities, QueryResult } from './types'

// Explicitly convert a filter condition value to text for string comparisons
function conditionText(value: unknown): string {
	if (typeof value === 'string') return value
	if (value === null || value === undefined) return String(value)
	if (typeof value === 'number' || typeof value === 'boolean') return String(value)
	if (value instanceof Date) return value.toString()
	if (Array.isArray(value)) return value.map(v => conditionText(v)).join(',')
	return JSON.stringify(value)
}

export interface BuoyQueryOptions {
	type?: string
	isDart?: boolean
	isDrifting?: boolean
}

// Format wave height with color coding
const formatWaveHeight = (height: number | null) => {
	if (height === null) return <span className="text-white/30">—</span>

	let colorClass = "text-emerald-400"
	if (height >= 4) colorClass = "text-red-400"
	else if (height >= 2) colorClass = "text-amber-400"
	else if (height >= 1) colorClass = "text-cyan-400"

	return <span className={colorClass}>{height.toFixed(1)}m</span>
}

// Format wind speed with color coding
const formatWindSpeed = (speed: number | null) => {
	if (speed === null) return <span className="text-white/30">—</span>

	// Convert m/s to knots for display
	const knots = speed * 1.944
	let colorClass = "text-emerald-400"
	if (knots >= 34) colorClass = "text-red-400" // Gale force
	else if (knots >= 22) colorClass = "text-amber-400" // Strong breeze
	else if (knots >= 11) colorClass = "text-cyan-400" // Moderate breeze

	return <span className={colorClass}>{knots.toFixed(0)} kts</span>
}

// Format temperature
const formatTemp = (temp: number | null) => {
	if (temp === null) return <span className="text-white/30">—</span>

	let colorClass = "text-cyan-400"
	if (temp >= 25) colorClass = "text-red-400"
	else if (temp >= 20) colorClass = "text-amber-400"
	else if (temp >= 10) colorClass = "text-emerald-400"
	else if (temp < 5) colorClass = "text-blue-400"

	return <span className={colorClass}>{temp.toFixed(1)}°C</span>
}

export class BuoyDataProvider implements IDataProvider {
	readonly id: string
	readonly name: string
	readonly type = 'custom' as const
	private queryFn: (options: BuoyQueryOptions) => Promise<BuoyEntry[]>
	private cachedData: BuoyEntry[] = []
	private isInitializedFlag = false

	constructor(config: {
		id: string
		name: string
		queryFn: (options: BuoyQueryOptions) => Promise<BuoyEntry[]>
	}) {
		this.id = config.id
		this.name = config.name
		this.queryFn = config.queryFn
	}

	async initialize(): Promise<void> {
		if (this.isInitializedFlag) return
		// Fetch initial data
		this.cachedData = await this.queryFn({})
		this.isInitializedFlag = true
	}

	async refreshData(options?: BuoyQueryOptions): Promise<void> {
		this.cachedData = await this.queryFn(options || {})
	}

	readonly capabilities: ProviderCapabilities = {
		supportsFiltering: true,
		supportsSorting: true,
		supportsPagination: true,
		supportsFullTextSearch: true,
		supportsRegex: true,
		supportsGeometry: true,
		supportsInsert: false,
		supportsUpdate: false,
		supportsDelete: false,
		supportsTransactions: false,
		supportsIndexing: false,
		supportsAggregation: false,
		supportsJoins: false,
		isRemote: false,
		supportsStreaming: false,
		supportsCaching: false,
	}

	private columnMetadata: ColumnMetadata[] = [
		{
			id: 'station_id',
			name: 'Station',
			priority: 1,
			minWidth: 100,
			type: 'string',
			nullable: false,
			render: (value: unknown, row: DataRow) => {
				const buoy = row as unknown as BuoyEntry
				return (
					<div className="flex items-center gap-2">
						<span className="font-mono text-xs">{value as string}</span>
						{buoy.dart && (
							<AlertTriangle className="w-3 h-3 text-orange-400" />
						)}
					</div>
				)
			}
		},
		{
			id: 'name',
			name: 'Name',
			priority: 1,
			minWidth: 180,
			type: 'string',
			nullable: false,
			render: (value: unknown) => (
				<div className="font-medium truncate max-w-[200px]" title={value as string}>
					{value as string}
				</div>
			)
		},
		{
			id: 'type',
			name: 'Type',
			priority: 4,
			minWidth: 110,
			type: 'string',
			nullable: true,
			render: (value: unknown) => {
				if (!value) return <span className="text-white/60 text-xs">—</span>
				return <span className="text-white/60 text-xs capitalize">{value as string}</span>
			}
		},
		{
			id: 'wave_height',
			name: 'Waves',
			priority: 2,
			minWidth: 90,
			type: 'number',
			nullable: true,
			render: (_value: unknown, row: DataRow) => {
				const buoy = row as unknown as BuoyEntry
				return formatWaveHeight(buoy.latest_observation?.wave_height ?? null)
			}
		},
		{
			id: 'wind_speed',
			name: 'Wind',
			priority: 2,
			minWidth: 90,
			type: 'number',
			nullable: true,
			render: (_value: unknown, row: DataRow) => {
				const buoy = row as unknown as BuoyEntry
				return formatWindSpeed(buoy.latest_observation?.wind_speed ?? null)
			}
		},
		{
			id: 'water_temp',
			name: 'SST',
			priority: 3,
			minWidth: 90,
			type: 'number',
			nullable: true,
			render: (_value: unknown, row: DataRow) => {
				const buoy = row as unknown as BuoyEntry
				return formatTemp(buoy.latest_observation?.water_temp ?? null)
			}
		},
		{
			id: 'last_observation_time',
			name: 'Updated',
			priority: 4,
			minWidth: 130,
			type: 'string',
			nullable: true,
			render: (_value: unknown, row: DataRow) => {
				const buoy = row as unknown as BuoyEntry
				const time = buoy.latest_observation?.time
				if (!time) return <span className="text-white/30">—</span>
				const date = new Date(time)
				return (
					<span className="text-white/60 text-xs">
						{formatDistanceToNow(date, { addSuffix: true })}
					</span>
				)
			}
		}
	]

	async getColumns(): Promise<ColumnMetadata[]> {
		return this.columnMetadata
	}

	async query(query: DataQuery): Promise<QueryResult> {
		let data = [...this.cachedData]

		// Apply filters
		if (query.filters) {
			data = this.applyFilters(data, query.filters)
		}

		// Apply search
		if (query.search) {
			const searchLower = query.search.query.toLowerCase()
			data = data.filter(buoy =>
				buoy.station_id.toLowerCase().includes(searchLower) ||
				buoy.name.toLowerCase().includes(searchLower) ||
				buoy.type?.toLowerCase().includes(searchLower) ||
				buoy.owner?.toLowerCase().includes(searchLower)
			)
		}

		// Apply sorting; default to biggest seas first so the most interesting
		// stations on the planet right now surface at the top
		const sort = query.sort && query.sort.length > 0
			? query.sort
			: [{ column: 'wave_height', direction: 'desc' as const }]
		{
			const sortSpec = sort[0]
			data.sort((a, b) => {
				const aVal = this.getColumnValue(a, sortSpec.column)
				const bVal = this.getColumnValue(b, sortSpec.column)

				if (aVal === null || aVal === undefined) return 1
				if (bVal === null || bVal === undefined) return -1

				const compareResult = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
				return sortSpec.direction === 'asc' ? compareResult : -compareResult
			})
		}

		// Apply pagination
		const total = data.length
		const offset = query.offset || 0
		const limit = query.limit || 100
		const paginatedData = data.slice(offset, offset + limit)

		return {
			rows: paginatedData as unknown as DataRow[],
			totalCount: total,
			offset,
			limit,
			hasMore: offset + limit < total,
		}
	}

	private getColumnValue(buoy: BuoyEntry, columnId: string): string | number | null | undefined {
		switch (columnId) {
			case 'station_id':
				return buoy.station_id
			case 'name':
				return buoy.name
			case 'type':
				return buoy.type
			case 'wave_height':
				return buoy.latest_observation?.wave_height
			case 'wind_speed':
				return buoy.latest_observation?.wind_speed
			case 'water_temp':
				return buoy.latest_observation?.water_temp
			case 'last_observation_time':
				return buoy.latest_observation?.time
			default:
				return null
		}
	}

	private applyFilters(data: BuoyEntry[], filterGroup: FilterGroup): BuoyEntry[] {
		return data.filter(buoy => this.rowMatchesFilterGroup(buoy, filterGroup))
	}

	private rowMatchesFilterGroup(buoy: BuoyEntry, filterGroup: FilterGroup): boolean {
		const results = filterGroup.conditions.map((condition) => {
			if ('conditions' in condition) {
				// Nested filter group
				return this.rowMatchesFilterGroup(buoy, condition)
			}

			const value = this.getColumnValue(buoy, condition.column)

			switch (condition.operator) {
				case 'equals':
					return value === condition.value
				case 'not_equals':
					return value !== condition.value
				case 'contains':
					return String(value).toLowerCase().includes(conditionText(condition.value).toLowerCase())
				case 'starts_with':
					return String(value).toLowerCase().startsWith(conditionText(condition.value).toLowerCase())
				case 'ends_with':
					return String(value).toLowerCase().endsWith(conditionText(condition.value).toLowerCase())
				case 'greater_than':
					return Number(value) > Number(condition.value)
				case 'less_than':
					return Number(value) < Number(condition.value)
				case 'greater_than_or_equal':
					return Number(value) >= Number(condition.value)
				case 'less_than_or_equal':
					return Number(value) <= Number(condition.value)
				case 'is_null':
					return value === null || value === undefined
				case 'is_not_null':
					return value !== null && value !== undefined
				default:
					return true
			}
		})

		// Apply AND/OR logic
		return filterGroup.operator === 'AND'
			? results.every((r: boolean) => r)
			: results.some((r: boolean) => r)
	}

	async getStats(): Promise<DataStats> {
		return {
			totalRows: this.cachedData.length,
			columnCount: this.columnMetadata.length,
		}
	}

	async count(_filters?: FilterGroup): Promise<number> {
		return this.cachedData.length
	}

	async insert(_rows: DataRow[]): Promise<void> {
		throw new Error('Insert not supported for buoy data provider')
	}

	async update(_filters: FilterGroup, _updates: Partial<DataRow>): Promise<number> {
		throw new Error('Update not supported for buoy data provider')
	}

	async delete(_filters: FilterGroup): Promise<number> {
		throw new Error('Delete not supported for buoy data provider')
	}

	async executeRaw(_sql: string): Promise<QueryResult> {
		throw new Error('Raw SQL not supported for buoy data provider')
	}

	isInitialized(): boolean {
		return this.isInitializedFlag
	}

	async close(): Promise<void> {
		this.cachedData = []
		this.isInitializedFlag = false
	}
}
